import type { ApiPinflGetResponse } from '@/shared/types/api/pinfl';
import { roundMoney } from '@/shared/utils/currency';

export type PinflLimitView = {
  // Total taken, including any in-progress amount passed as extraUsed.
  used: number;
  // Ceiling from the dynamic API `limit` (not an env constant).
  ceiling: number;
  // Unified over-limit threshold: trust the backend flag, or fall back to the
  // displayed used exceeding the ceiling (strictly greater — not >=).
  isOver: boolean;
};

// True when a form row counts toward the limit on top of the excluded-parcel
// baseline. Unsaved rows (no server id) always count; saved rows the server
// does not count (created before the usage window, e.g. last month or before
// an admin reset) are skipped, so baseline + live total == server usage.
export const isCountedInLimit = (
  limit: Pick<ApiPinflGetResponse, 'uncounted_product_ids'> | null | undefined,
  serverId?: string | null,
): boolean => !serverId || !(limit?.uncounted_product_ids ?? []).includes(serverId);

// Derives the displayed used/ceiling and the over-limit flag from a PINFL limit
// response. `extraUsed` is any not-yet-committed amount to add on top of the
// backend's used_amount (e.g. the in-progress declaration total).
export const computePinflLimitView = (
  limit: ApiPinflGetResponse,
  extraUsed = 0,
): PinflLimitView => {
  const ceiling = limit.limit;
  // Cents-rounded like the server's compute_used_amount, so an exact-limit
  // total is not flagged over by float noise.
  const used = roundMoney(limit.used_amount + extraUsed);
  const isOver = limit.is_over_limit || used > ceiling;
  return { used, ceiling, isOver };
};
